"use client";

import { useEffect, useMemo } from "react";
import { LuRefreshCw } from "react-icons/lu";
import { useLocalStorage } from "usehooks-ts";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { TimePeriodLabels } from "@/contexts/filters-sort-context";
import { useIsClient } from "@/hooks/use-is-client";
import { APP_NAME_GENERIC, DESO_TOKEN_NAME } from "@/utils/constants";
import { pluralize } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { LocalStorageKeys } from "@/utils/local-storage-keys";

import { TimePeriod } from "../../discover-sort-constants";
import {
  normalizeWidgetTimePeriod,
  Past1H,
  WidgetPreset,
  type WidgetTimePeriod,
} from "./sidebar-top-results-widget-time-period";
import { TopCoinsWidgetView } from "./top-coins-widget-view";
import { TopEarnersWidgetView } from "./top-earners-widget-view";
import { TopJoinersWidgetView } from "./top-joiners-widget-view";
import { useRefreshDiscoverWidgetCategory } from "./use-discover-widget-query";

export { WidgetPreset } from "./sidebar-top-results-widget-time-period";

const WidgetPresetLabel = {
  [WidgetPreset.topJoiners]: "Top Joiners",
  [WidgetPreset.topEarners]: "Top Earners",
  [WidgetPreset.topCoins]: "Top Tokens",
};

const DiscoverWidgetTimePeriodLabels = {
  ...TimePeriodLabels,
  [Past1H]: "Past 1 Hour",
};

export const SidebarTopResultsWidget = ({
  initialPreset = WidgetPreset.topCoins,
  hideTitle = false,
  hideCategorySelector = false,
}: {
  hideTitle?: boolean;
  hideCategorySelector?: boolean;
  initialPreset?: WidgetPreset;
}) => {
  const [preset, setPreset] = useLocalStorage<WidgetPreset>(
    LocalStorageKeys.sidebarWidgetPreset,
    initialPreset,
    {
      initializeWithValue: false,
    },
  );
  const [selectedTimePeriod, setSelectedTimePeriod] =
    useLocalStorage<WidgetTimePeriod>(
      LocalStorageKeys.sidebarWidgetTimePeriod,
      TimePeriod.AllTime,
      {
        initializeWithValue: false,
      },
    );

  const isClient = useIsClient();
  const normalizedSelectedTimePeriod = useMemo(
    () => normalizeWidgetTimePeriod(preset, selectedTimePeriod),
    [preset, selectedTimePeriod],
  );
  const normalizedStandardTimePeriod =
    normalizedSelectedTimePeriod === Past1H
      ? TimePeriod.Past24H
      : normalizedSelectedTimePeriod;
  const { refresh, refreshing } = useRefreshDiscoverWidgetCategory({
    preset,
    timePeriod:
      preset === WidgetPreset.topCoins
        ? normalizedSelectedTimePeriod
        : normalizedStandardTimePeriod,
  });

  useEffect(() => {
    if (!isClient) {
      return;
    }

    if (normalizedSelectedTimePeriod !== selectedTimePeriod) {
      setSelectedTimePeriod(normalizedSelectedTimePeriod);
    }
  }, [
    isClient,
    normalizedSelectedTimePeriod,
    selectedTimePeriod,
    setSelectedTimePeriod,
  ]);

  const infoTooltipText = useMemo(() => {
    switch (preset) {
      case WidgetPreset.topJoiners:
        return `The users who have recently joined ${APP_NAME_GENERIC} sorted by their total assets.`;
      case WidgetPreset.topCoins:
        return `The ${pluralize(Infinity, DESO_TOKEN_NAME, { includeCount: false })} that have the highest trading volume recently.`;
      case WidgetPreset.topEarners:
        return `The users who have earned the most on ${APP_NAME_GENERIC} recently.`;
    }
  }, [preset]);

  const filterTimePeriods = useMemo(() => {
    const commonFilters: Array<TimePeriod | typeof Past1H> = [
      TimePeriod.Past24H,
      TimePeriod.Past7D,
      TimePeriod.Past30D,
    ];

    if (preset === WidgetPreset.topCoins) {
      commonFilters.unshift(Past1H);
    } else {
      commonFilters.push(TimePeriod.AllTime);
    }

    return commonFilters;
  }, [preset]);

  return (
    <div
      className="
      w-full rounded-2xl border border-border-light bg-transparent
    "
    >
      {!hideTitle && (
        <div className="flex items-center justify-between p-4 pb-2">
          <div
            className="
              text-[10px] font-semibold tracking-widest text-muted uppercase
            "
          >
            DISCOVER
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="Refresh"
              aria-label="Refresh discover widget"
              className="
                size-7 rounded-full text-muted hover:bg-transparent
                hover:text-muted-foreground
              "
              disabled={refreshing}
              onClick={() => void refresh()}
            >
              <LuRefreshCw
                className={cn("size-3.5", refreshing && "animate-spin")}
              />
            </Button>
            <InfoTooltip text={infoTooltipText} />
          </div>
        </div>
      )}
      <div className="flex flex-col gap-0 border-b border-border-light text-sm">
        {!hideCategorySelector && (
          <div className="flex items-center gap-2">
            <Select
              value={preset}
              onValueChange={(preset: WidgetPreset) => setPreset(preset)}
            >
              <SelectTrigger
                className="
                  unforce-radius w-full rounded-none border-x-0 border-t-0
                  border-border-light bg-transparent px-4 py-3
                  hover:border-border-light hover:bg-transparent
                  focus:border-border-light focus:bg-transparent
                  focus:ring-0
                  data-popup-open:border-border-light
                  data-popup-open:bg-transparent
                "
              >
                <div className="flex w-full items-center justify-between">
                  <div className="relative">{WidgetPresetLabel[preset]}</div>
                </div>
              </SelectTrigger>
              <SelectContent>
                {[
                  WidgetPreset.topJoiners,
                  WidgetPreset.topEarners,
                  WidgetPreset.topCoins,
                ].map((preset) => (
                  <SelectItem key={preset} value={preset}>
                    {WidgetPresetLabel[preset]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="w-full px-4">
          <Select
            value={normalizedSelectedTimePeriod}
            onValueChange={(timePeriod: WidgetTimePeriod) =>
              setSelectedTimePeriod(timePeriod)
            }
          >
            <SelectTrigger
              className="
                unforce-radius rounded-none border-none bg-transparent px-0 py-2
                leading-none text-muted
                hover:border-transparent hover:bg-transparent
                focus:border-transparent focus:bg-transparent
                focus:ring-0
                data-popup-open:border-transparent
                data-popup-open:bg-transparent
              "
            >
              <span
                className="
                  w-full text-left text-xs underline-offset-4
                  hover:underline
                "
              >
                {DiscoverWidgetTimePeriodLabels[normalizedSelectedTimePeriod]}
              </span>
            </SelectTrigger>
            <SelectContent>
              {filterTimePeriods.map((timePeriod) => (
                <SelectItem key={timePeriod} value={timePeriod}>
                  {DiscoverWidgetTimePeriodLabels[timePeriod]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {preset === WidgetPreset.topCoins && (
        <>
          {normalizedSelectedTimePeriod !== TimePeriod.AllTime && (
            <TopCoinsWidgetView
              selectedTimePeriod={normalizedSelectedTimePeriod}
            />
          )}
        </>
      )}

      {preset === WidgetPreset.topJoiners && (
        <TopJoinersWidgetView
          selectedTimePeriod={normalizedStandardTimePeriod}
        />
      )}

      {preset === WidgetPreset.topEarners && (
        <TopEarnersWidgetView
          preset={preset}
          selectedTimePeriod={normalizedStandardTimePeriod}
        />
      )}
    </div>
  );
};
